/**
 * StationDropdown - Grouped station selector by railway line
 *
 * Features:
 * - Shows all stations grouped by railway line on focus
 * - Line headers display railway logo + line name
 * - Individual stations show name only (no logo)
 * - Auto-filters as user types (local filtering)
 * - Click-outside closes dropdown
 */

import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { observer } from "mobx-react-lite";
import { Train } from "lucide-react";
import { Input } from "@/shared/ui/components";
import { Box, Text } from "@/shared/ui/base";
import { RailwayLineLogo } from "../RailwayLineLogo";
import { useGeoStations } from "../../hooks";
import type { Station, SupportedLanguage, LocalizedName } from "../../types";

// =============================================================================
// Types
// =============================================================================

export interface StationDropdownProps {
  citySlug?: string;
  lang: SupportedLanguage;
  onSelect: (station: Station) => void;
  disabled?: boolean;
  selectedIds?: string[];
  placeholder?: string;
}

/** Group of stations belonging to the same railway line */
interface LineGroup {
  lineKey: string;
  lineName: LocalizedName;
  lineCode?: string;
  color?: string;
  stations: Station[];
}

// =============================================================================
// Utility Functions
// =============================================================================

/**
 * Groups stations by their railway lines.
 * A station may appear under multiple lines if it serves multiple lines.
 */
function groupStationsByLine(
  stations: Station[],
  lang: SupportedLanguage,
): LineGroup[] {
  const lineMap = new Map<string, LineGroup>();
  const noLineStations: Station[] = [];

  for (const station of stations) {
    // If station has no lines, collect separately
    if (!station.lines || station.lines.length === 0) {
      noLineStations.push(station);
      continue;
    }

    for (const line of station.lines) {
      const key = `${line.line_code ?? ""}:${line.line_name.ja}`;

      if (!lineMap.has(key)) {
        lineMap.set(key, {
          lineKey: key,
          lineName: line.line_name,
          lineCode: line.line_code,
          color: line.color,
          stations: [],
        });
      }

      const group = lineMap.get(key)!;
      // Avoid duplicates
      if (!group.stations.find((s) => s.id === station.id)) {
        group.stations.push(station);
      }
    }
  }

  // Sort groups by line name
  const groups = Array.from(lineMap.values()).sort((a, b) =>
    a.lineName[lang].localeCompare(b.lineName[lang]),
  );

  // Sort stations within each group
  for (const group of groups) {
    group.stations.sort((a, b) => a.name[lang].localeCompare(b.name[lang]));
  }

  // Add "Other Stations" group if there are stations without lines
  if (noLineStations.length > 0) {
    groups.push({
      lineKey: "__other__",
      lineName: { ja: "その他", en: "Other Stations", ru: "Другие станции" },
      lineCode: undefined,
      color: undefined,
      stations: noLineStations.sort((a, b) =>
        a.name[lang].localeCompare(b.name[lang]),
      ),
    });
  }

  return groups;
}

/**
 * Filter stations by query matching station name
 */
function filterStationsByQuery(
  stations: Station[],
  query: string,
  _lang: SupportedLanguage,
): Station[] {
  const normalizedQuery = query.toLowerCase().trim();
  if (!normalizedQuery) return stations;

  return stations.filter((station) => {
    // Search in all language variants
    return (
      station.name.ja.toLowerCase().includes(normalizedQuery) ||
      station.name.en.toLowerCase().includes(normalizedQuery) ||
      station.name.ru.toLowerCase().includes(normalizedQuery)
    );
  });
}

// =============================================================================
// Component
// =============================================================================

export const StationDropdown = observer((props: StationDropdownProps) => {
  const {
    citySlug,
    lang,
    onSelect,
    disabled,
    selectedIds = [],
    placeholder,
  } = props;

  // State
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Hook to access stations
  const { stations, loading, loadStations } = useGeoStations({
    lang,
    citySlug,
  });

  // Load stations when dropdown opens or citySlug changes
  useEffect(() => {
    if (isOpen && citySlug) {
      loadStations();
    }
  }, [isOpen, citySlug, loadStations]);

  // Filter out already-selected stations
  const availableStations = useMemo(() => {
    return stations.filter((s) => !selectedIds.includes(s.id));
  }, [stations, selectedIds]);

  // Filter by query and group by line
  const groupedStations = useMemo(() => {
    const filtered = filterStationsByQuery(availableStations, query, lang);
    return groupStationsByLine(filtered, lang);
  }, [availableStations, query, lang]);

  // Get display name based on language
  const getDisplayName = useCallback(
    (item: { name?: LocalizedName; lineName?: LocalizedName }) => {
      const name = item.name || item.lineName;
      if (!name) return "";
      if (lang === "ja") return name.ja;
      if (lang === "en") return name.en;
      return name.ru || name.en;
    },
    [lang],
  );

  // Handle station selection
  const handleSelect = useCallback(
    (station: Station) => {
      onSelect(station);
      setQuery("");
      setIsOpen(false);
    },
    [onSelect],
  );

  // Handle input focus
  const handleFocus = useCallback(() => {
    if (!disabled) {
      setIsOpen(true);
    }
  }, [disabled]);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  // Check if line has valid logo data
  const hasValidLogo = (group: LineGroup): boolean => {
    return Boolean(group.lineCode && group.color);
  };

  return (
    <Box ref={containerRef} className="relative">
      {/* Search Input */}
      <Input
        ref={inputRef}
        placeholder={placeholder || "Search stations..."}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onFocus={handleFocus}
        disabled={disabled}
        autoComplete="off"
      />

      {/* Dropdown */}
      {isOpen && (
        <Box
          className="absolute z-50 top-full left-0 right-0 mt-1 max-h-80 overflow-y-auto
            bg-white border border-gray-200 rounded-lg shadow-lg"
        >
          {/* Loading state */}
          {loading && (
            <Box className="px-4 py-3 text-center">
              <Text variant="body" color="secondary" className="text-sm">
                Loading stations...
              </Text>
            </Box>
          )}

          {/* No results */}
          {!loading && groupedStations.length === 0 && (
            <Box className="px-4 py-3 text-center">
              <Text variant="body" color="secondary" className="text-sm">
                {query ? "No stations found" : "No stations available"}
              </Text>
            </Box>
          )}

          {/* Grouped stations list */}
          {!loading && groupedStations.length > 0 && (
            <Box as="ul" className="py-1">
              {groupedStations.map((group) => (
                <Box as="li" key={group.lineKey}>
                  {/* Line Header */}
                  <Box
                    className="sticky top-0 px-3 py-2 bg-gray-50 border-b border-gray-100
                      flex items-center gap-2"
                  >
                    {hasValidLogo(group) ? (
                      <RailwayLineLogo
                        lineCode={group.lineCode!}
                        color={group.color!}
                        size={20}
                      />
                    ) : (
                      <Train className="w-5 h-5 text-gray-400" />
                    )}
                    <Text
                      variant="body"
                      weight="semibold"
                      className="text-sm text-gray-700"
                    >
                      {getDisplayName({ lineName: group.lineName })}
                    </Text>
                    <Text
                      variant="body"
                      color="secondary"
                      className="text-xs ml-auto"
                    >
                      ({group.stations.length})
                    </Text>
                  </Box>

                  {/* Stations List */}
                  <Box as="ul">
                    {group.stations.map((station) => (
                      <Box as="li" key={station.id}>
                        <button
                          type="button"
                          onClick={() => handleSelect(station)}
                          className="w-full px-4 py-2 pl-9 text-left text-sm text-gray-700
                            hover:bg-gray-100 transition-colors focus:outline-none
                            focus:bg-gray-100"
                        >
                          {getDisplayName({ name: station.name })}
                        </button>
                      </Box>
                    ))}
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
});
